import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RateReviewIcon from "@mui/icons-material/RateReview";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Box, SxProps, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useIntl } from "react-intl";
import { RequirementSetStatus } from "./requirementSetStatus";
import styles from "./styles";

type NodeState = "reached" | "current" | "notReached";

/**
 * Draft → Review → Active, with Archived as an off-ramp from any of them. A status, not a wizard: no
 * step numbers, a passed state shows a check and the current one its own icon. Active is where
 * authoring ends, so an Active set reads as complete rather than as a last step still to do.
 */
const TRACK: { status: RequirementSetStatus; labelKey: string }[] = [
  { status: RequirementSetStatus.Draft, labelKey: "draft" },
  { status: RequirementSetStatus.Review, labelKey: "review" },
  { status: RequirementSetStatus.Active, labelKey: "activeLabel" },
];

const STATUS_ICON: Record<RequirementSetStatus, ReactNode> = {
  [RequirementSetStatus.Draft]: <EditNoteIcon fontSize="inherit" />,
  [RequirementSetStatus.Review]: <RateReviewIcon fontSize="inherit" />,
  [RequirementSetStatus.Active]: <VerifiedIcon fontSize="inherit" />,
  [RequirementSetStatus.Archived]: <BlockIcon fontSize="inherit" />,
};

const CURRENT_ICON_STYLE: Record<RequirementSetStatus, SxProps> = {
  [RequirementSetStatus.Draft]: styles.lifecycleIconDraft,
  [RequirementSetStatus.Review]: styles.lifecycleIconReview,
  [RequirementSetStatus.Active]: styles.lifecycleIconActive,
  [RequirementSetStatus.Archived]: styles.lifecycleIconArchived,
};

type LifecycleNodeProps = {
  status: RequirementSetStatus;
  label: string;
  state: NodeState;
  disabled: boolean;
  line?: "reached" | "notReached" | "offRamp";
};

const LifecycleNode = ({ status, label, state, disabled, line }: LifecycleNodeProps) => {
  const iconStyle =
    state === "reached"
      ? styles.lifecycleIconReached
      : state === "current"
        ? CURRENT_ICON_STYLE[status]
        : {};

  const labelStyle = disabled
    ? styles.lifecycleLabelDisabled
    : state === "current"
      ? styles.lifecycleLabelCurrent
      : state === "reached"
        ? styles.lifecycleLabelReached
        : styles.lifecycleLabel;

  const lineStyle =
    line === "offRamp"
      ? styles.lifecycleOffRamp
      : line === "reached"
        ? styles.lifecycleLineReached
        : styles.lifecycleLine;

  return (
    <Box
      component="li"
      sx={styles.lifecycleNode}
      aria-current={state === "current" ? "step" : undefined}
    >
      {line && <Box component="span" aria-hidden sx={lineStyle} />}
      <Box component="span" aria-hidden sx={[styles.lifecycleIcon, iconStyle] as SxProps}>
        {state === "reached" && <CheckIcon fontSize="inherit" />}
        {state === "current" && STATUS_ICON[status]}
      </Box>
      <Typography component="span" variant="body2" sx={labelStyle}>
        {label}
      </Typography>
    </Box>
  );
};

const LifecycleTrack = ({ status }: { status: RequirementSetStatus }) => {
  const { formatMessage: t } = useIntl();
  const archived = status === RequirementSetStatus.Archived;
  const currentIndex = TRACK.findIndex((step) => step.status === status);

  const stateAt = (index: number): NodeState => {
    if (archived || index > currentIndex) return "notReached";
    return index < currentIndex ? "reached" : "current";
  };

  return (
    // role="list" restated: Safari drops the list role from an <ol> styled list-style: none.
    <Box
      component="ol"
      role="list"
      aria-label={t({ id: "lifecycle" })}
      sx={styles.lifecycleTrack}
    >
      {TRACK.map((step, index) => (
        <LifecycleNode
          key={step.status}
          status={step.status}
          label={t({ id: step.labelKey })}
          state={stateAt(index)}
          disabled={archived}
          line={
            index === 0
              ? undefined
              : !archived && index <= currentIndex
                ? "reached"
                : "notReached"
          }
        />
      ))}

      {archived && (
        <LifecycleNode
          status={RequirementSetStatus.Archived}
          label={t({ id: "archived" })}
          state="current"
          disabled={false}
          line="offRamp"
        />
      )}
    </Box>
  );
};

export default LifecycleTrack;
